import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { type Observable, Subject } from "rxjs";
import {
	type BookTourExistingLeadInput,
	type BookTourNewLeadInput,
	type RescheduleTourInput,
	type Tour,
	type UpdateTourInput,
} from "src/app/schema";
import { ApiService, CognitoService } from "src/app/services";

@Injectable({
	providedIn: "root",
})
export class ToursService {
	private readonly _httpClient = inject(HttpClient);
	private readonly _api = inject(ApiService);
	private readonly _cognitoService = inject(CognitoService);

	public selectedTour$: Subject<Tour | null> = new Subject<Tour | null>();

	public bookTourForNewLead(tour: BookTourNewLeadInput): Observable<Tour> {
		return this._httpClient.post<Tour>(
			`${this._api.calendaringApiEndpoint}/new-lead/book-tour`,
			tour,
		);
	}

	public bookTourExistingLead(
		tour: BookTourExistingLeadInput,
	): Observable<Tour> {
		return this._httpClient.post<Tour>(
			`${this._api.calendaringApiEndpoint}/leads/${tour.leadId}/tours/book`,
			tour,
		);
	}

	public rescheduleTour(tour: RescheduleTourInput) {
		return this._httpClient.put<Tour>(
			`${this._api.calendaringApiEndpoint}/tours/${tour.id}/reschedule`,
			tour,
		);
	}

	public reassignTour(tourId: string, userId: string): Observable<Tour> {
		return this._httpClient.put<Tour>(
			`${this._api.calendaringApiEndpoint}/tours/${tourId}/reassign`,
			{ userId: userId },
		);
	}

	public unassignTour(tourId: string): Observable<Tour> {
		return this._httpClient.put<Tour>(
			`${this._api.calendaringApiEndpoint}/tours/${tourId}/unassign`,
			{},
		);
	}

	public confirmTour(tourId: string, userId: string): Observable<Tour> {
		return this._httpClient.put<Tour>(
			`${this._api.calendaringApiEndpoint}/tours/${tourId}/confirm`,
			{ userId: userId },
		);
	}

	public cancelTour(tourId: string, cancellationReasons: string[]) {
		return this._httpClient.put<Tour>(
			`${this._api.calendaringApiEndpoint}/tours/${tourId}/cancel`,
			{
				cancellationReasons: cancellationReasons,
				userId: this._cognitoService.user().id,
			},
		);
	}

	public updateTour(tour: UpdateTourInput): Observable<Tour> {
		return this._httpClient.put<Tour>(
			`${this._api.calendaringApiEndpoint}/tour`,
			tour,
		);
	}

	public getTour(tourId: string): Observable<Tour> {
		return this._httpClient.get<Tour>(
			`${this._api.calendaringApiEndpoint}/tour/${tourId}`,
		);
	}

	public markAsShow(tourId: string): Observable<Tour> {
		return this._httpClient.put<Tour>(
			`${this._api.calendaringApiEndpoint}/tours/${tourId}/show`,
			{},
		);
	}

	public markAsNoShow(tourId: string): Observable<Tour> {
		return this._httpClient.put<Tour>(
			`${this._api.calendaringApiEndpoint}/tours/${tourId}/no-show`,
			{},
		);
	}
}
